import { useForm } from "react-hook-form";
import { useState, useEffect, useRef } from "react";
import { getApi } from '../../providers/AuthContext';
import { Link, useHistory } from "react-router-dom";
import { motion } from 'framer-motion';
import { Save, ArrowLeft, Info, CreditCard, Layout, User, Loader2, Check } from 'lucide-react';

import * as yup from 'yup';
import { yupResolver } from "@hookform/resolvers/yup";

import { PapInput, PapTemplateSelect, PapSelect, PapErrors } from '../FormElements';

interface CardFormProps {
  card?: any;
}

// Step indicator for visual progress
const StepIndicator = ({ step, label, isActive, isComplete }: { step: number; label: string; isActive: boolean; isComplete: boolean }) => (
  <div className="flex items-center gap-2">
    <div className={`
      w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300
      ${isComplete
        ? 'bg-success text-success-foreground'
        : isActive
          ? 'bg-accent text-accent-foreground shadow-soft-sm'
          : 'bg-muted text-muted-foreground'
      }
    `}>
      {isComplete ? <Check className="w-3.5 h-3.5" /> : step}
    </div>
    <span className={`text-xs font-medium transition-colors duration-200 hidden sm:inline ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
      {label}
    </span>
  </div>
);

const StepConnector = () => (
  <div className="flex-1 h-px bg-border/50 mx-1 hidden sm:block" />
);

const CardForm: React.FC<CardFormProps> = ({ card }) => {
  const schema = yup
    .object()
    .shape({
      title: yup.string().required('Title is required').label('Title'),
      template_id: yup.string().required('Please select a template').label('Template'),
      child_id: yup.string().required('Please select a child').label('Child')
    });

  const history = useHistory();

  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const [childOptions, setChildOptions] = useState<any[]>([]);
  const [templateOptions, setTemplateOptions] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  const api = getApi();
  const formRef = useRef<HTMLFormElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(schema)
  });

  // Watch form fields for step indicator
  const titleValue = watch('title');
  const templateValue = watch('template_id');
  const childValue = watch('child_id');

  const resetErrors = () => {
    setPageErrors([]);
  };

  const resetPage = () => {
    reset();
    resetErrors();
  };

  const loadChildren = async () => {
    try {
      const result = await api.get('/api/v1/children');
      setChildOptions(result.children);
    } catch {
      // Silently handle errors
    }
  };

  const loadTemplates = async () => {
    try {
      const result = await api.get('/api/v1/templates');
      setTemplateOptions(result.templates);
    } catch {
      // Silently handle errors
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setDataLoading(true);
      await Promise.all([loadTemplates(), loadChildren()]);
      if (card) {
        reset(card);
      }
      setDataLoading(false);
    };
    loadData();
  }, [card]);

  const cancel = () => {
    resetPage();
  };

  const saveCard = async (data: any) => {
    const card_data = {
      card: data
    };

    let result = null;

    try {
      if (card && card.id) {
        result = await api.put(`/api/v1/cards/${card.id}`, card_data);
      } else {
        result = await api.post('/api/v1/cards', card_data);
      }
    } catch (ex: any) {
      try {
        const exResponse = await ex.response?.json();
        setPageErrors([exResponse?.error || 'An error occurred']);
      } catch {
        setPageErrors([ex.message || 'An error occurred']);
      }
    }

    if (result) {
      resetPage();
      history.push('/cards');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Info banner */}
      <div className="mx-4 mt-4 lg:mx-6 lg:mt-6 mb-4">
        <div className="p-4 rounded-xl bg-gradient-to-r from-accent/5 via-primary/5 to-secondary/5 dark:from-accent/10 dark:via-primary/10 dark:to-secondary/10 border border-border/40 flex items-start gap-3">
          <Info className="w-5 h-5 text-accent shrink-0 mt-0.5" />
          <p className="text-sm text-foreground/80 leading-relaxed">
            Ensure your child's profile is completed <Link to="/children" className="text-accent font-medium hover:underline transition-colors">here</Link> before creating a card.
            Profile updates will automatically reflect when the card is scanned.
          </p>
        </div>
      </div>

      <div className="form-container">
        <div className="form-wrapper">
          {/* Form card */}
          <div className="bg-card border border-border/40 rounded-2xl shadow-soft-sm overflow-hidden">
            {/* Accent bar */}
            <div className="h-1.5 bg-gradient-to-r from-accent via-primary to-secondary" />

            <div className="p-6 sm:p-8">
              {/* Form header */}
              <div className="flex items-center gap-3 mb-5 pb-5 border-b border-border/30">
                <div className="w-10 h-10 rounded-xl bg-accent/10 dark:bg-accent/20 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    {card ? 'Edit Card' : 'Create New Card'}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {card ? 'Update your card details' : 'Design a trading card for your child'}
                  </p>
                </div>
              </div>

              {/* Step indicator */}
              {!dataLoading && (
                <div className="flex items-center mb-6 px-2">
                  <StepIndicator step={1} label="Title" isActive={!titleValue} isComplete={!!titleValue} />
                  <StepConnector />
                  <StepIndicator step={2} label="Template" isActive={!!titleValue && !templateValue} isComplete={!!templateValue} />
                  <StepConnector />
                  <StepIndicator step={3} label="Child" isActive={!!titleValue && !!templateValue && !childValue} isComplete={!!childValue} />
                </div>
              )}

              <PapErrors errors={pageErrors} />

              {dataLoading ? (
                <div className="space-y-6">
                  <div>
                    <div className="skeleton h-4 w-16 mb-3 rounded-lg" />
                    <div className="skeleton h-12 w-full rounded-xl" />
                  </div>
                  <div>
                    <div className="skeleton h-4 w-24 mb-3 rounded-lg" />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="skeleton h-40 rounded-xl" />
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="skeleton h-4 w-16 mb-3 rounded-lg" />
                    <div className="skeleton h-12 w-full rounded-xl" />
                  </div>
                </div>
              ) : (
                <form ref={formRef} noValidate onSubmit={handleSubmit(saveCard)}>
                  {/* Step 1: Title */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <CreditCard className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Card Title</span>
                    </div>
                    <PapInput fieldLabel="Title" fieldName="title" placeholder="Maddison's Superman Card" required={true} formBinding={register("title")} error={errors.title} />
                  </div>

                  {/* Step 2: Template */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Layout className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Choose Template</span>
                    </div>
                    <PapTemplateSelect options={templateOptions} fieldLabel="Template" fieldName="template_id" setValue={setValue as (name: string, value: any) => void} value={card ? card.template_id : ''} formBinding={register("template_id")} required={true} error={errors.template_id} />
                  </div>

                  {/* Step 3: Child */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Link to Child</span>
                    </div>
                    <PapSelect options={childOptions} fieldLabel="Child" fieldName="child_id" optionLabel="first_name" placeholder="Select a Child" required={true} formBinding={register("child_id")} error={errors.child_id} />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 mt-2 border-t border-border/30">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-tertiary py-2.5 px-6 rounded-xl gap-2 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                    <Link
                      to="/cards"
                      onClick={cancel}
                      className="btn-light py-2.5 px-5 rounded-xl no-underline text-inherit gap-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Go Back
                    </Link>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CardForm;
